import React, {useState, useEffect} from 'react';
import {ProdutoObject, ProdutoVendido} from "../../models/Produto";
import {IonButton, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonTitle, IonToolbar} from "@ionic/react";
import styles from "./ProdutoVendidoAdd.module.css";
import classNames from "classnames";
import FieldView from "../FieldView";
import {forms} from "../../lib/forms";
import {formatNumber, string2Number} from "../../lib/utils";
import {useAppDispatch, useAppSelector, usePrevious} from "../../lib/hooks";
import { createOutline } from 'ionicons/icons';
import {actions} from "../../lib/app";

interface ProdutoVendidoAddProps {
  produtos: ProdutoObject[];
  isOpen: boolean;
  onDidDismiss: () => void;
  onCommit: (produto: ProdutoVendido) => void;
}

const ProdutoVendidoAdd: React.FC<ProdutoVendidoAddProps> = (props) => {
  const user = useAppSelector(state => state.app.user);
  const {produtos, isOpen, onDidDismiss, onCommit} = props;
  const dispatch = useAppDispatch();
  const wasOpen = usePrevious(isOpen);
  const [selecionado, setSelecionado] = useState<ProdutoObject>();
  const [quantidade, setQuantidade] = useState<number>();
  const [valor, setValor] = useState<string>();
  
  // reset
  useEffect(() => {
    if (wasOpen && !isOpen) {
      console.log('reset ProdutoVendidoAdd');
      setSelecionado(undefined);
      setQuantidade(undefined);
      setValor(undefined);
    }
  }, [isOpen, wasOpen]);

  const onChangeProduto = (_p: ProdutoObject) => {
    setSelecionado(_p);
    const {regional} = user;
    // console.log('onChangeProduto', regional, _p.precos[regional]);
    setValor(formatNumber(_p.precos[regional]));
  }

  const onConfirmar = () => {
    if (quantidade) {
      if (quantidade < 1) {
        dispatch(actions.notifyError('Quantidade inválida.'));
        return;
      } else if (quantidade > 9999) {
        dispatch(actions.notifyError('Quantidade inválida, máximo de 9999.'));
        return;
      }
    }

    if (selecionado && quantidade) {
      let _valor = valor ? string2Number(valor) : 0;
      if (_valor === undefined)
        _valor = 0;
      if (_valor < 0) {
        dispatch(actions.notifyError('O registro desse produto não parece válido, o valor não pode ser negativo.'));
        return;
      }
      // console.log({...selecionado, quantidade, valor: _valor});
      onCommit({...selecionado, quantidade, valor: _valor});
    }
  }

  const onChangeQuantidade = (e: any) => {
    let val;
    if (e.target.value) {
      val = Number.parseInt(e.target.value);
      if (val < 1)
        val = 1;
    }
    setQuantidade(val);
  }

  const onChangeValor = (e: any) => {
    setValor(e.target.value);
  }

  useEffect(() => {
    setValor(forms.utils.format.currency(valor));
  }, [valor]);

  return (
    <>
      <IonModal
        isOpen={isOpen}
        onDidDismiss={onDidDismiss}
        className={classNames(styles.modal, "modal-as-alert")}
        keyboardClose={true}
        showBackdrop={true}
        backdropDismiss={true}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              Adicionar produto
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="flex flex-col h-full overflow-y-hidden">
            {!selecionado &&
              <SelectProduto
                produtos={produtos}
                onChange={onChangeProduto}
              />
            }

            {selecionado &&
              <div className="flex flex-col h-full">
                <div className='ion-padding flex-grow'>
                  <div className='flex items-center'>
                    <div className='flex-grow'>
                      <FieldView
                        label="Produto"
                        value={selecionado.label}
                      />
                    </div>
                    <div>
                      <IonButton
                        size={'small'}
                        fill={'clear'}
                        onClick={() => {
                          setSelecionado(undefined);
                        }}
                        color={'medium'}
                      >
                        <IonIcon icon={createOutline} slot="icon-only"/>
                    </IonButton>
                    </div>
                  </div>
                  <div>
                    <IonItem>
                      <IonLabel position="fixed">
                        Quantidade
                      </IonLabel>
                      <IonInput
                        value={quantidade}
                        onIonChange={onChangeQuantidade}
                        type="number"
                        inputmode='numeric'
                        min={1}
                        max={9999}
                        maxlength={5}
                        autofocus
                      />
                    </IonItem>

                    <IonItem>
                      <IonLabel position="fixed">
                        Valor un (R$)
                      </IonLabel>
                      <IonInput
                        disabled
                        value={valor}
                        onIonChange={onChangeValor}
                        inputMode="numeric"
                        debounce={250}
                      />
                    </IonItem>
                  </div>
                </div>
                <div className="flex gap-4 mx-1">
                  <div className="w-1/2">
                    <IonButton
                      onClick={onDidDismiss}
                      fill="clear"
                      // size="small"
                      expand="block"
                    >
                      Fechar
                    </IonButton>
                  </div>
                  <div className="w-1/2">
                    <IonButton
                      // size="small"
                      onClick={onConfirmar}
                      disabled={!selecionado || !quantidade}
                      expand="block"
                    >
                      Confirmar
                    </IonButton>
                  </div>
                </div>
              </div>
            }
          </div>
        </IonContent>
      </IonModal>
    </>
  );
}

export default ProdutoVendidoAdd;


interface SelectProdutoProps {
  produtos: ProdutoObject[];
  onChange: (produto: ProdutoObject) => void;
}

const SelectProduto: React.FC<SelectProdutoProps> = (props) => {
  const {produtos, onChange} = props;
  const [filteredCollection, setFilteredCollection] = useState<ProdutoObject[]>([]);
  const [termo, setTermo] = useState('');

  // reset
  useEffect(() => {
    if (!termo) {
      setFilteredCollection([...produtos]);
    }
  }, [termo, produtos]);

  const onChangeTermo = (e: any) => {
    const _termo = e.detail.value.toLowerCase();

    let filteredCollection: any[];
    if (_termo)
      filteredCollection = produtos.filter(
        i => i.sku.toLowerCase().includes(_termo) || i.label.toLowerCase().includes(_termo));
    else
      filteredCollection = [...produtos];

    setFilteredCollection(filteredCollection);
    setTermo(_termo);
  };

  const onClickItem = (_produto: ProdutoObject) => {
    onChange(_produto);
  };

  return (
    <>
      <div>
        <IonItem>
          <IonInput
            placeholder="Procurar"
            debounce={20}
            value={termo}
            onIonChange={onChangeTermo}
          />
        </IonItem>
      </div>
      <div className="overflow-y-auto flex flex-col flex-grow">
        {filteredCollection.length && filteredCollection.length <= 150 ? (
          <IonList>
            {
              filteredCollection && filteredCollection.map((i: any) => (
                <IonItem
                  key={i.sku}
                  button={true}
                  detail={false}
                  onClick={() => onClickItem(i)}
                  lines="none"
                >
                  <IonLabel>
                    {i.label}
                  </IonLabel>
                </IonItem>
              ))
            }
          </IonList>
        ) : (
          <div className="flex justify-center text-xs opacity-50">
            {termo ? (
              <div>Nenhum item encontrado.</div>
            ) : (
              <div>Digite sua busca para selecionar.</div>
            )
            }
          </div>
        )
        }
      </div>
    </>
  );
}
